// Fatia S5b: preferências de avisos do usuário nesta empresa (tipo × canal + número de WhatsApp).
import { useEffect, useState } from 'react'
import { Mail, MessageCircle, Smartphone } from 'lucide-react'
import type { ChannelPrefs, NotificationChannel } from '@impulso/shared/api/notifications'
import { useCompany } from '../../lib/company'
import { errorMessage } from '../../lib/api'
import { ErrorState, Loading, PageHead, Switch } from '../../ui'
import { useToast } from '../../ui/toast'
import CompanySettingsTabs from '../../features/account/CompanySettingsTabs'
import { useNotificationPrefs, useSaveNotificationPrefs } from '../../features/notifications/api'

const CHANNELS: Array<{ key: NotificationChannel; label: string; Icon: typeof Mail }> = [
  { key: 'in_app', label: 'No app', Icon: Smartphone },
  { key: 'email', label: 'E-mail', Icon: Mail },
  { key: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle },
]

function formatPhone(digits: string | null): string {
  if (!digits) return ''
  const d = digits.startsWith('55') && digits.length >= 12 ? digits.slice(2) : digits
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return digits
}

export default function SettingsNotifications() {
  const company = useCompany()
  const q = useNotificationPrefs(company.id)
  const save = useSaveNotificationPrefs(company.id)
  const toast = useToast()
  const [prefs, setPrefs] = useState<Record<string, ChannelPrefs>>({})
  const [phone, setPhone] = useState('')
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (!q.data || dirty) return
    setPrefs(Object.fromEntries(q.data.rows.map((r) => [r.kind, { ...r.channels }])))
    setPhone(formatPhone(q.data.whatsappNumber))
  }, [q.data, dirty])

  if (q.isLoading) return <><PageHead title="Configurações" /><CompanySettingsTabs /><div className="card"><Loading lines={6} /></div></>
  if (q.error || !q.data) return <><PageHead title="Configurações" /><CompanySettingsTabs /><ErrorState error={q.error} onRetry={() => q.refetch()} /></>

  const hasPhone = phone.replace(/\D/g, '').length >= 10
  const toggle = (kind: string, ch: NotificationChannel, v: boolean) => {
    setDirty(true)
    setPrefs((p) => ({ ...p, [kind]: { ...(p[kind] as ChannelPrefs), [ch]: v } }))
  }
  const submit = () =>
    save.mutate(
      { prefs, whatsappNumber: phone.trim() ? phone : null },
      {
        onSuccess: () => { setDirty(false); toast('Preferências de avisos salvas.') },
        onError: (e) => toast(errorMessage(e)),
      },
    )

  return (
    <>
      <PageHead title="Configurações" sub={`Escolha como você quer ser avisado sobre ${company.name}. Vale só para você.`} />
      <CompanySettingsTabs />

      <div className="stack">
        <div className="card flush">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Aviso</th>
                  {CHANNELS.map((c) => (
                    <th key={c.key} style={{ textAlign: 'center', padding: '10px 6px' }}>
                      <span className="row" style={{ gap: 4, justifyContent: 'center' }} title={c.label}><c.Icon size={14} aria-hidden="true" /><span>{c.label}</span></span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {q.data.rows.map((r) => (
                  <tr key={r.kind}>
                    <td style={{ padding: '12px 12px 12px 16px' }}>
                      <div className="strong">{r.label}</div>
                      <div className="small muted">{r.hint}</div>
                    </td>
                    {CHANNELS.map((c) => (
                      <td key={c.key} style={{ textAlign: 'center', padding: '12px 8px' }}>
                        <span style={{ display: 'inline-flex' }}>
                          <Switch
                            label={`${r.label} por ${c.label}`}
                            on={!!prefs[r.kind]?.[c.key] && (c.key !== 'whatsapp' || hasPhone)}
                            disabled={c.key === 'whatsapp' && !hasPhone}
                            onChange={(v) => toggle(r.kind, c.key, v)}
                          />
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-head"><span className="ic green"><MessageCircle size={17} /></span><h3>WhatsApp</h3></div>
          <div className="field" style={{ maxWidth: 320 }}>
            <label htmlFor="wa">Número com DDD</label>
            <input
              id="wa"
              className="input"
              inputMode="tel"
              autoComplete="tel"
              placeholder="(31) 99999-8888"
              value={phone}
              onChange={(e) => { setDirty(true); setPhone(e.target.value) }}
            />
          </div>
          <p className="small muted mt-8">
            {hasPhone ? 'Ligue o WhatsApp na tabela acima para os avisos que quiser receber por lá.' : 'Cadastre um número para poder receber avisos pelo WhatsApp.'}
          </p>
          {!q.data.whatsappAvailable && (
            <div className="callout amber mt-12 small">O envio pelo WhatsApp ainda está sendo ativado. Você já pode deixar tudo configurado.</div>
          )}
        </div>

        <div className="form-actions">
          <button className="btn primary" onClick={submit} disabled={!dirty || save.isPending}>{save.isPending ? 'Salvando…' : 'Salvar'}</button>
          {!dirty && <span className="small muted">Mude algum aviso para salvar.</span>}
        </div>
      </div>
    </>
  )
}
